import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import api from "../../Utils/Axios";

const Subscriptions = () => {
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [newCategory, setNewCategory] = useState("");
  const [newSubcategory, setNewSubcategory] = useState("");
  const [newSpecialty, setNewSpecialty] = useState("");
  const [parentCategoryId, setParentCategoryId] = useState("");
  const [parentSubcategoryId, setParentSubcategoryId] = useState("");
  const [specialtyCategoryId, setSpecialtyCategoryId] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [subcategoryFilter, setSubcategoryFilter] = useState("");
  const [specialtyFilter, setSpecialtyFilter] = useState("");
  const [pricing, setPricing] = useState({ trialDays: 90, pricePerDayUSD: 2.5, pricePerMonthUSD: 75 });
  const [footer, setFooter] = useState({
    supportLine: "+1 (410) 555-1212",
    locationText: "Maryland, USA",
    copyrightText: "Copyright 2026 Health Professional Workers Pool",
  });

  const load = async () => {
    try {
      const [catRes, subRes, specRes, pricingRes, footerRes] = await Promise.all([
        api.get("/api/admin/categories"),
        api.get("/api/admin/subcategories"),
        api.get("/api/admin/specialties"),
        api.get("/api/admin/pricing"),
        api.get("/api/admin/footer-settings"),
      ]);
      setCategories(catRes.data.categories || []);
      setSubcategories(subRes.data?.subcategories || []);
      setSpecialties(specRes.data?.specialties || []);
      setPricing(pricingRes.data.pricing || { trialDays: 90, pricePerDayUSD: 2.5, pricePerMonthUSD: 75 });
      setFooter((prev) => ({ ...prev, ...(footerRes.data.footer || {}) }));
    } catch (err) {
      toast.error("Failed to load admin settings");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const addCategory = async () => {
    if (!newCategory.trim()) return;
    try {
      await api.post("/api/admin/categories", { name: newCategory.trim() });
      toast.success("Category added");
      setNewCategory("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to add category");
    }
  };

  const deleteCategory = async (id) => {
    try {
      await api.delete(`/api/admin/categories/${id}`);
      toast.success("Category deleted");
      load();
    } catch (err) {
      toast.error("Failed to delete category");
    }
  };

  const addSubcategory = async () => {
    if (!parentCategoryId || !newSubcategory.trim()) return;
    try {
      await api.post("/api/admin/subcategories", {
        categoryId: parentCategoryId,
        name: newSubcategory.trim(),
      });
      toast.success("Subcategory added");
      setNewSubcategory("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to add subcategory");
    }
  };

  const deleteSubcategory = async (id) => {
    try {
      await api.delete(`/api/admin/subcategories/${id}`);
      toast.success("Subcategory deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to delete subcategory");
    }
  };

  const addSpecialty = async () => {
    if (!parentSubcategoryId || !newSpecialty.trim()) return;
    try {
      await api.post("/api/admin/specialties", {
        subcategoryId: parentSubcategoryId,
        name: newSpecialty.trim(),
      });
      toast.success("Specialty added");
      setNewSpecialty("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to add specialty");
    }
  };

  const deleteSpecialty = async (id) => {
    try {
      await api.delete(`/api/admin/specialties/${id}`);
      toast.success("Specialty deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to delete specialty");
    }
  };

  const savePricing = async () => {
    try {
      await api.put("/api/admin/pricing", pricing);
      toast.success("Pricing updated");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save pricing");
    }
  };

  const saveFooter = async () => {
    try {
      await api.put("/api/admin/footer-settings", footer);
      toast.success("Footer updated");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save footer");
    }
  };

  const moveOtherToEnd = (items) => {
    const other = [];
    const rest = [];
    (items || []).forEach((item) => {
      if (String(item?.name || "").trim().toLowerCase() === "other") {
        other.push(item);
      } else {
        rest.push(item);
      }
    });
    return [...rest, ...other];
  };

  const filteredCategories = useMemo(() => {
    const q = categoryFilter.trim().toLowerCase();
    if (!q) return categories;
    return categories.filter((cat) => String(cat.name || "").toLowerCase().includes(q));
  }, [categories, categoryFilter]);

  const filteredSubcategories = useMemo(() => {
    const q = subcategoryFilter.trim().toLowerCase();
    if (!q) return moveOtherToEnd(subcategories);
    const filtered = subcategories.filter(
      (sub) =>
        String(sub.name || "").toLowerCase().includes(q) ||
        String(sub.categoryName || "").toLowerCase().includes(q)
    );
    return moveOtherToEnd(filtered);
  }, [subcategories, subcategoryFilter]);

  const subcategoriesForSpecialty = useMemo(() => {
    if (!specialtyCategoryId) return moveOtherToEnd(subcategories);
    const filtered = subcategories.filter((sub) => String(sub.categoryId || "") === String(specialtyCategoryId));
    return moveOtherToEnd(filtered);
  }, [subcategories, specialtyCategoryId]);

  const filteredSpecialties = useMemo(() => {
    const q = specialtyFilter.trim().toLowerCase();
    if (!q) return specialties;
    return specialties.filter(
      (spec) =>
        String(spec.name || "").toLowerCase().includes(q) ||
        String(spec.subcategoryName || "").toLowerCase().includes(q) ||
        String(spec.categoryName || "").toLowerCase().includes(q)
    );
  }, [specialties, specialtyFilter]);

  return (
    <div className="col-lg-12">
      <div className="dashboard-inner">
        <div className="row g-4">
          <div className="col-lg-6">
            <div className="table-wrapper2">
              <div className="title-and-btn">
                <div className="title">
                  <h4>Job Categories</h4>
                </div>
              </div>
              <div className="row g-2 mb-25">
                <div className="col-md-8">
                  <div className="form-inner">
                    <label style={{ display: "block", marginBottom: 8, fontWeight: 600 }}>
                      Category Name
                    </label>
                    <input
                      type="text"
                      style={{
                        width: "100%",
                        height: 50,
                        border: "1.5px solid #cfd4dc",
                        borderRadius: 8,
                        padding: "0 14px",
                        background: "#fff",
                      }}
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value)}
                      placeholder="e.g. Nurse, Surgeon, Lab Technician"
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <button className="primry-btn-2 lg-btn w-100" onClick={addCategory}>
                    Add Category
                  </button>
                </div>
              </div>
              <div className="row g-2 mb-25">
                <div className="col-md-12">
                  <div className="form-inner">
                    <label style={{ display: "block", marginBottom: 8, fontWeight: 600 }}>
                      Search Categories
                    </label>
                    <input
                      type="text"
                      style={{
                        width: "100%",
                        height: 46,
                        border: "1.5px solid #cfd4dc",
                        borderRadius: 8,
                        padding: "0 14px",
                        background: "#fff",
                      }}
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                      placeholder="Search by name"
                    />
                  </div>
                </div>
              </div>
              <div style={{ maxHeight: 280, overflowY: "auto" }}>
                <table className="eg-table table job-list-table mb-0">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Created</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCategories.length === 0 ? (
                      <tr>
                        <td colSpan="3">No categories found.</td>
                      </tr>
                    ) : (
                      filteredCategories.map((cat) => (
                        <tr key={cat._id}>
                          <td>{cat.name}</td>
                          <td>{new Date(cat.createdAt).toLocaleDateString()}</td>
                          <td>
                            <button className="primry-btn-1" onClick={() => deleteCategory(cat._id)}>
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="col-lg-6">
            <div className="table-wrapper2">
              <div className="title-and-btn">
                <div className="title">
                  <h4>Job Specialties</h4>
                </div>
              </div>
              <div className="row g-2 mb-25">
                <div className="col-md-12">
                  <div className="form-inner">
                    <label style={{ display: "block", marginBottom: 8, fontWeight: 600 }}>
                      Category
                    </label>
                    <select
                      className="select1"
                      style={{
                        width: "100%",
                        height: 50,
                        border: "1.5px solid #cfd4dc",
                        borderRadius: 8,
                        padding: "0 14px",
                        background: "#fff",
                      }}
                      value={specialtyCategoryId}
                      onChange={(e) => {
                        setSpecialtyCategoryId(e.target.value);
                        setParentSubcategoryId("");
                      }}
                    >
                      <option value="">Select category</option>
                      {categories.map((cat) => (
                        <option key={cat._id} value={cat._id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-md-12">
                  <div className="form-inner">
                    <label style={{ display: "block", marginBottom: 8, fontWeight: 600 }}>
                      Subcategory
                    </label>
                    <select
                      className="select1"
                      style={{
                        width: "100%",
                        height: 50,
                        border: "1.5px solid #cfd4dc",
                        borderRadius: 8,
                        padding: "0 14px",
                        background: "#fff",
                      }}
                      value={parentSubcategoryId}
                      onChange={(e) => setParentSubcategoryId(e.target.value)}
                      disabled={!specialtyCategoryId}
                    >
                      <option value="">Select subcategory</option>
                      {subcategoriesForSpecialty.map((sub) => (
                        <option key={sub._id} value={sub._id}>
                          {sub.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-md-8">
                  <div className="form-inner">
                    <label style={{ display: "block", marginBottom: 8, fontWeight: 600 }}>
                      Specialty Name
                    </label>
                    <input
                      type="text"
                      style={{
                        width: "100%",
                        height: 50,
                        border: "1.5px solid #cfd4dc",
                        borderRadius: 8,
                        padding: "0 14px",
                        background: "#fff",
                      }}
                      value={newSpecialty}
                      onChange={(e) => setNewSpecialty(e.target.value)}
                      placeholder="e.g. Cardiology, Pulmonology"
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <button className="primry-btn-2 lg-btn w-100" onClick={addSpecialty}>
                    Add Specialty
                  </button>
                </div>
              </div>
              <div className="row g-2 mb-25">
                <div className="col-md-12">
                  <div className="form-inner">
                    <label style={{ display: "block", marginBottom: 8, fontWeight: 600 }}>
                      Search Specialties
                    </label>
                    <input
                      type="text"
                      style={{
                        width: "100%",
                        height: 46,
                        border: "1.5px solid #cfd4dc",
                        borderRadius: 8,
                        padding: "0 14px",
                        background: "#fff",
                      }}
                      value={specialtyFilter}
                      onChange={(e) => setSpecialtyFilter(e.target.value)}
                      placeholder="Search by specialty, subcategory, or category"
                    />
                  </div>
                </div>
              </div>
              <div style={{ maxHeight: 280, overflowY: "auto" }}>
                <table className="eg-table table job-list-table mb-0">
                  <thead>
                    <tr>
                      <th>Specialty</th>
                      <th>Subcategory</th>
                      <th>Category</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSpecialties.length === 0 ? (
                      <tr>
                        <td colSpan="4">No specialties found.</td>
                      </tr>
                    ) : (
                      filteredSpecialties.map((spec) => (
                        <tr key={spec._id}>
                          <td>{spec.name}</td>
                          <td>{spec.subcategoryName}</td>
                          <td>{spec.categoryName}</td>
                          <td>
                            <button className="primry-btn-1" onClick={() => deleteSpecialty(spec._id)}>
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="col-lg-6">
            <div className="table-wrapper2">
              <div className="title-and-btn">
                <div className="title">
                  <h4>Job Subcategories</h4>
                </div>
              </div>
              <div className="row g-2 mb-25">
                <div className="col-md-12">
                  <div className="form-inner">
                    <label style={{ display: "block", marginBottom: 8, fontWeight: 600 }}>
                      Parent Category
                    </label>
                    <select
                      className="select1"
                      style={{
                        width: "100%",
                        height: 50,
                        border: "1.5px solid #cfd4dc",
                        borderRadius: 8,
                        padding: "0 14px",
                        background: "#fff",
                      }}
                      value={parentCategoryId}
                      onChange={(e) => setParentCategoryId(e.target.value)}
                    >
                      <option value="">Select parent category</option>
                      {categories.map((cat) => (
                        <option key={cat._id} value={cat._id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-md-8">
                  <div className="form-inner">
                    <label style={{ display: "block", marginBottom: 8, fontWeight: 600 }}>
                      Subcategory Name
                    </label>
                    <input
                      type="text"
                      style={{
                        width: "100%",
                        height: 50,
                        border: "1.5px solid #cfd4dc",
                        borderRadius: 8,
                        padding: "0 14px",
                        background: "#fff",
                      }}
                      value={newSubcategory}
                      onChange={(e) => setNewSubcategory(e.target.value)}
                      placeholder="e.g. ICU Nurse, Pediatric Surgeon"
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <button className="primry-btn-2 lg-btn w-100" onClick={addSubcategory}>
                    Add Subcategory
                  </button>
                </div>
              </div>
              <div className="row g-2 mb-25">
                <div className="col-md-12">
                  <div className="form-inner">
                    <label style={{ display: "block", marginBottom: 8, fontWeight: 600 }}>
                      Search Subcategories
                    </label>
                    <input
                      type="text"
                      style={{
                        width: "100%",
                        height: 46,
                        border: "1.5px solid #cfd4dc",
                        borderRadius: 8,
                        padding: "0 14px",
                        background: "#fff",
                      }}
                      value={subcategoryFilter}
                      onChange={(e) => setSubcategoryFilter(e.target.value)}
                      placeholder="Search by subcategory or category"
                    />
                  </div>
                </div>
              </div>
              <div style={{ maxHeight: 280, overflowY: "auto" }}>
                <table className="eg-table table job-list-table mb-0">
                  <thead>
                    <tr>
                      <th>Subcategory</th>
                      <th>Category</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSubcategories.length === 0 ? (
                      <tr>
                        <td colSpan="3">No subcategories found.</td>
                      </tr>
                    ) : (
                      filteredSubcategories.map((sub) => (
                        <tr key={sub._id}>
                          <td>{sub.name}</td>
                          <td>{sub.categoryName}</td>
                          <td>
                            <button className="primry-btn-1" onClick={() => deleteSubcategory(sub._id)}>
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="col-lg-6">
            <div className="table-wrapper2">
              <div className="title-and-btn">
                <div className="title">
                  <h4>Posting Pricing Rules</h4>
                </div>
              </div>
              <div className="row g-3">
                <div className="col-md-12">
                  <div className="form-inner">
                    <label>Free Trial Days</label>
                    <input
                      type="number"
                      min={0}
                      disabled
                      value={pricing.trialDays}
                      onChange={(e) => setPricing((prev) => ({ ...prev, trialDays: Number(e.target.value) }))}
                    />
                    <small style={{ color: "#666" }}>Fixed at 90 days (3 months).</small>
                  </div>
                </div>
                <div className="col-md-12">
                  <div className="form-inner">
                    <label>Premium Daily Price (USD)</label>
                    <input
                      type="number"
                      min={0}
                      value={pricing.pricePerDayUSD}
                      onChange={(e) =>
                        setPricing((prev) => ({ ...prev, pricePerDayUSD: Number(e.target.value) }))
                      }
                    />
                  </div>
                </div>
                <div className="col-md-12">
                  <div className="form-inner">
                    <label>Premium Monthly Price (USD)</label>
                    <input
                      type="number"
                      min={0}
                      value={pricing.pricePerMonthUSD}
                      onChange={(e) =>
                        setPricing((prev) => ({ ...prev, pricePerMonthUSD: Number(e.target.value) }))
                      }
                    />
                  </div>
                </div>
                <div className="col-md-12">
                  <div className="dashboard-sidebar" style={{ marginTop: 8 }}>
                    <div className="single-widget">
                      <div className="widget-title">
                        <h6>Current Rule Preview</h6>
                      </div>
                      <ul className="recent-activitys">
                        <li>
                          <div className="content">
                            <h6>Trial: {pricing.trialDays} day(s)</h6>
                            <p>Hospitals can post for free during trial period.</p>
                          </div>
                        </li>
                        <li>
                          <div className="content">
                            <h6>Premium Daily: ${pricing.pricePerDayUSD} / day</h6>
                            <p>Charged once per day when posting under Premium Daily.</p>
                          </div>
                        </li>
                        <li>
                          <div className="content">
                            <h6>Premium Monthly: ${pricing.pricePerMonthUSD} / month</h6>
                            <p>Charged once per month when posting under Premium Monthly.</p>
                          </div>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div className="col-md-12">
                  <button className="primry-btn-2 lg-btn w-100" onClick={savePricing}>
                    Save Pricing Settings
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-12">
            <div className="table-wrapper2">
              <div className="title-and-btn">
                <div className="title">
                  <h4>Footer Settings</h4>
                </div>
              </div>
              <div className="row g-3">
                <div className="col-md-4">
                  <div className="form-inner">
                    <label>Support Line</label>
                    <input
                      value={footer.supportLine || ""}
                      onChange={(e) => setFooter((prev) => ({ ...prev, supportLine: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-inner">
                    <label>Location</label>
                    <input
                      value={footer.locationText || ""}
                      onChange={(e) => setFooter((prev) => ({ ...prev, locationText: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-inner">
                    <label>Copyright Text</label>
                    <input
                      value={footer.copyrightText || ""}
                      onChange={(e) => setFooter((prev) => ({ ...prev, copyrightText: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="col-md-12">
                  <button className="primry-btn-2 lg-btn w-100" onClick={saveFooter}>
                    Save Footer Settings
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Subscriptions;
